import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

/**
 * Configuration options for the Text-to-Speech synthesis request.
 */
export interface TextToSpeechOptions {
  languageCode?: string;
  name?: string;
  ssmlGender?: 'MALE' | 'FEMALE' | 'NEUTRAL';
  audioEncoding?: 'MP3' | 'LINEAR16' | 'OGG_OPUS';
}

/**
 * Configuration options for the Speech-to-Text recognition request.
 */
export interface SpeechToTextConfig {
    encoding?: 'ENCODING_UNSPECIFIED' | 'LINEAR16' | 'FLAC' | 'MULAW' | 'AMR' | 'AMR_WB' | 'OGG_OPUS' | 'SPEEX_WITH_HEADER_BYTE' | 'WEBM_OPUS';
    sampleRateHertz?: number;
    languageCode?: string;
    enableAutomaticPunctuation?: boolean;
    model?: string;
}

/**
 * An Angular service to interact with Google Cloud Speech and Text-to-Speech APIs from the front-end.
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleSpeechService {
  private http = inject(HttpClient);

  private apiKey: string = '';
  private ttsUrl = 'https://texttospeech.googleapis.com/v1/text:synthesize';
  private sttUrl = 'https://speech.googleapis.com/v1/speech:recognize';
  isWebVoice: boolean = false;

  /**
   * Sets the Google Cloud API key to use for subsequent requests.
   * @param apiKey Your Google Cloud API key.
   */
  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Synthesizes speech from plain text.
   * @param text The text to synthesize.
   * @param options Configuration for the voice and audio output.
   * @returns An Observable that emits an ArrayBuffer containing the audio data.
   */
  textToSpeech(
    text: string,
    options?: TextToSpeechOptions
  ): Observable<ArrayBuffer> {
    const requestBody = {
      input: { text: text },
      voice: {
        languageCode: options?.languageCode || 'zh-TW',
        name: options?.name,
        ssmlGender: options?.ssmlGender || 'FEMALE',
      },
      audioConfig: {
        audioEncoding: options?.audioEncoding || 'MP3',
      },
    };

    return this.http.post<{ audioContent: string }>(`${this.ttsUrl}?key=${this.apiKey}`, requestBody)
      .pipe(
        map(response => this.base64ToArrayBuffer(response.audioContent))
      );
  }

  /**
   * Synthesizes speech from SSML markup.
   * @param ssml The SSML text to synthesize.
   * @param options Configuration for the voice and audio output.
   * @returns An Observable that emits an ArrayBuffer containing the audio data.
   */
  ssmlToAudio(
    ssml: string,
    options?: TextToSpeechOptions
  ): Observable<ArrayBuffer> {
    const requestBody = {
      input: { ssml: ssml },
      voice: {
        languageCode: options?.languageCode || 'zh-TW',
        name: options?.name,
        ssmlGender: options?.ssmlGender || 'FEMALE',
      },
      audioConfig: {
        audioEncoding: options?.audioEncoding || 'MP3',
      },
    };
     return this.http.post<{ audioContent: string }>(`${this.ttsUrl}?key=${this.apiKey}`, requestBody)
      .pipe(
        map(response => this.base64ToArrayBuffer(response.audioContent))
      );
  }

  /**
   * Transcribes a short audio file (< 1 minute).
   * @param audioBlob The audio data as a Blob (e.g., from a microphone recording).
   * @param config Configuration for the speech recognition request.
   * @returns An Observable with the transcription results from the API.
   */
  speechToText(audioBlob: Blob, config: SpeechToTextConfig): Observable<any> {
    return new Observable(observer => {
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
            // Strip the data URL prefix to get just the base64-encoded data
            const base64Audio = (reader.result as string).split(',')[1];

            const requestBody = {
                config: {
                    encoding: config.encoding || 'WEBM_OPUS', // Common format for browser recordings
                    sampleRateHertz: config.sampleRateHertz, // It's best to provide this from the source
                    languageCode: config.languageCode || 'zh-TW',
                    enableAutomaticPunctuation: config.enableAutomaticPunctuation ?? true,
                    model: config.model
                },
                audio: {
                    content: base64Audio
                }
            };

            this.http.post(`${this.sttUrl}?key=${this.apiKey}`, requestBody).subscribe({
                next: (result) => observer.next(result),
                error: (err) => observer.error(err),
                complete: () => observer.complete()
            });
        };
        reader.onerror = (error) => {
            observer.error(error);
        };
    });
  }

  /**
   * Decodes a base64 string into an ArrayBuffer.
   * @param base64 The base64 encoded string.
   * @returns An ArrayBuffer.
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // --- Server-Side Methods (Not for Front-End) ---
  // The following methods from the original file are not suitable for a pure
  // front-end implementation. They require a server-side environment to handle
  // Node.js libraries (like @google-cloud/speech), manage service account
  // credentials securely, and process gRPC streams or long-running operations.

  /*
  async longRunningRecognize(gcsUri: string): Promise<string> {
    // This requires authenticating with service account credentials to access GCS
    // and to initiate a long-running operation, which is not secure on the client-side.
    console.error('longRunningRecognize is a server-side method and not implemented for front-end.');
    return Promise.reject('Not implemented for front-end');
  }
  */

  /*
  async streamSpeechToText(audioStream: any): Promise<string> {
    // Browser environments do not support the gRPC streaming used by the Node.js library.
    // A backend proxy would be needed to handle the stream from the client.
    console.error('streamSpeechToText is a server-side method and not implemented for front-end.');
    return Promise.reject('Not implemented for front-end');
  }
  */
}
